import { FiCheck, FiX } from 'react-icons/fi';
import './dynamicQuestionField.css';

/** Fecha de hoy en formato `YYYY-MM-DD`, para el atributo `min` de inputs `date`. */
function todayIsoDate() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}


/**
 * Renderiza una pregunta de un formulario dinámico por `slug`
 * (`boolean`, `radio`, `textarea`). No resuelve condiciones ni
 * validación de negocio — eso lo hace la vista con `utils/dynamicForm.js`.
 *
 * @param {{
 *   question: import('../../services/dynamicFormService').DynamicQuestion,
 *   value: any,
 *   error?: string,
 *   onChange: (value: any) => void,
 * }} props
 */
export default function DynamicQuestionField({ question, value, error, onChange }) {
  const fieldId = `dynamic-q-${question.id}`;

  return (
    <div className={`survey-field${error ? ' has-error' : ''}`}>
      <label className="survey-field__label" htmlFor={fieldId}>
        {question.label}
        {question.required && <span className="survey-field__required">*</span>}
      </label>

      {question.type === 'boolean' && (
        <div className="dynamic-boolean" id={fieldId} role="radiogroup" aria-label={question.label}>
          <button
            type="button"
            className={`dynamic-boolean__option${value === true ? ' is-selected' : ''}`}
            role="radio"
            aria-checked={value === true}
            onClick={() => onChange(true)}
          >
            <FiCheck /> {question.settings?.true_label ?? 'Sí'}
          </button>
          <button
            type="button"
            className={`dynamic-boolean__option${value === false ? ' is-selected' : ''}`}
            role="radio"
            aria-checked={value === false}
            onClick={() => onChange(false)}
          >
            <FiX /> {question.settings?.false_label ?? 'No'}
          </button>
        </div>
      )}

      {question.type === 'radio' && (
        <div className="survey-options" id={fieldId} role="radiogroup" aria-label={question.label}>
          {question.options.map((option) => (
            <button
              type="button"
              key={option.id}
              className={`survey-option${value === option.value ? ' is-selected' : ''}`}
              role="radio"
              aria-checked={value === option.value}
              onClick={() => onChange(option.value)}
            >
              <span className="survey-option__radio" aria-hidden="true" />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}

      {question.type === 'text' && (
        <input
          id={fieldId}
          className="survey-input"
          type="text"
          inputMode={question.settings?.input_mode || undefined}
          autoComplete={question.settings?.autocomplete || undefined}
          placeholder={question.settings?.placeholder ?? ''}
          maxLength={question.validation?.max || undefined}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {question.type === 'date' && (
        <input
          id={fieldId}
          className="survey-input"
          type="date"
          min={question.validation?.afterOrEqual === 'today' ? todayIsoDate() : undefined}
          placeholder={question.settings?.placeholder ?? ''}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {question.type === 'phone' && (
        <input
          id={fieldId}
          className="survey-input"
          type="tel"
          inputMode="tel"
          autoComplete={question.settings?.autocomplete || 'tel'}
          placeholder={question.settings?.placeholder ?? ''}
          maxLength={question.validation?.maxDigits || undefined}
          value={value || ''}
          onChange={(e) => onChange(e.target.value.replace(/[^\d+]/g, ''))}
        />
      )}

      {question.type === 'email' && (
        <input
          id={fieldId}
          className="survey-input"
          type="email"
          inputMode="email"
          autoComplete={question.settings?.autocomplete || 'email'}
          placeholder={question.settings?.placeholder ?? ''}
          maxLength={question.validation?.max || undefined}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {question.type === 'textarea' && (
        <>
          <textarea
            id={fieldId}
            className="survey-textarea"
            rows={question.settings?.rows ?? 4}
            placeholder={question.settings?.placeholder ?? ''}
            maxLength={question.validation?.max || undefined}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {question.validation?.max && (
            <span className="dynamic-textarea__counter">
              {(value || '').length}/{question.validation.max}
            </span>
          )}
        </>
      )}

      {error && <span className="survey-field__error">{error}</span>}
    </div>
  );
}
